import React from 'react';
import { TaskStatus, TaskHorizon } from '../types';
import { X } from 'lucide-react';

interface FilterPanelProps {
  statusFilter: TaskStatus[];
  horizonFilter: string[];
  tagsFilter: string[];
  availableTags: string[];
  taskTypeFilter: 'all' | 'parent_only' | 'child_only';
  onStatusToggle: (status: TaskStatus) => void;
  onHorizonToggle: (horizon: string) => void;
  onTagToggle: (tag: string) => void;
  onTaskTypeChange: (type: 'all' | 'parent_only' | 'child_only') => void;
  onClearFilters: () => void;
  onClose?: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  statusFilter,
  horizonFilter,
  tagsFilter,
  availableTags,
  taskTypeFilter,
  onStatusToggle,
  onHorizonToggle,
  onTagToggle,
  onTaskTypeChange,
  onClearFilters,
  onClose
}) => {
  const allStatuses = Object.values(TaskStatus);
  const hasActiveFilters = statusFilter.length < allStatuses.length ||
                          horizonFilter.length > 0 ||
                          tagsFilter.length > 0 ||
                          taskTypeFilter !== 'all';

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 sm:p-6 w-full">
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <h3 className="font-semibold text-gray-900 text-base sm:text-lg">Filters</h3>
        <div className="flex items-center space-x-2 sm:space-x-3">
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium px-2 sm:px-3 py-1 rounded-md hover:bg-blue-50 transition-colors"
            >
              Clear All
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1.5 sm:p-2 transition-all duration-200 touch-manipulation"
              aria-label="Close filters"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
        {/* Left Column */}
        <div className="space-y-4 sm:space-y-6">
          {/* Task Type Filter */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3 sm:mb-4 flex items-center text-sm sm:text-base">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              Task Type
              {taskTypeFilter !== 'all' && (
                <span className="ml-2 sm:ml-3 text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
                  {taskTypeFilter === 'parent_only' ? 'Parents' : 'Subtasks'}
                </span>
              )}
            </h4>
            <div className="space-y-2 sm:space-y-3 pl-4 sm:pl-8">
              {[
                { value: 'all', label: 'All Tasks' },
                { value: 'parent_only', label: 'Parent Tasks Only' },
                { value: 'child_only', label: 'Subtasks Only' }
              ].map(option => (
                <label key={option.value} className="flex items-center group cursor-pointer">
                  <input
                    type="radio"
                    name="taskType"
                    value={option.value}
                    checked={taskTypeFilter === option.value}
                    onChange={() => onTaskTypeChange(option.value as 'all' | 'parent_only' | 'child_only')}
                    className="border-gray-300 text-blue-600 focus:ring-blue-500 transition-colors w-4 h-4"
                  />
                  <span className={`ml-3 text-sm group-hover:text-gray-900 transition-colors ${
                    taskTypeFilter === option.value ? 'text-gray-900 font-medium' : 'text-gray-700'
                  }`}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4 flex items-center text-base">
              <svg className="w-5 h-5 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Status
              {statusFilter.length < allStatuses.length && (
                <span className="ml-3 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  {statusFilter.length}/{allStatuses.length}
                </span>
              )}
            </h4>
            <div className="space-y-3 pl-8">
              {allStatuses.map(status => (
                <label key={status} className="flex items-center group cursor-pointer">
                  <input
                    type="checkbox"
                    checked={statusFilter.includes(status)}
                    onChange={() => onStatusToggle(status)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-colors w-4 h-4"
                  />
                  <span className={`ml-3 text-sm capitalize group-hover:text-gray-900 transition-colors ${
                    statusFilter.includes(status) ? 'text-gray-900 font-medium' : 'text-gray-700'
                  }`}>
                    {status.replace('_', ' ')}
                    {(status === TaskStatus.COMPLETED || status === TaskStatus.CANCELED) && (
                      <span className="text-xs text-gray-500 ml-2">(hidden by default)</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
        
        {/* Right Column */}
        <div className="space-y-4 sm:space-y-6">
          {/* Horizon Filter */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3 sm:mb-4 flex items-center text-sm sm:text-base">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Horizon
              {horizonFilter.length > 0 && (
                <span className="ml-2 sm:ml-3 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                  {horizonFilter.length} selected
                </span>
              )}
            </h4>
            <div className="space-y-2 sm:space-y-3 pl-4 sm:pl-8">
              {Object.values(TaskHorizon).map(horizon => (
                <label key={horizon} className="flex items-center group cursor-pointer">
                  <input
                    type="checkbox"
                    checked={horizonFilter.includes(horizon)}
                    onChange={() => onHorizonToggle(horizon)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-colors w-4 h-4"
                  />
                  <span className="ml-3 text-sm text-gray-700 capitalize group-hover:text-gray-900 transition-colors">
                    {horizon === 'now' ? '🔴 Now' :
                     horizon === 'next' ? '🔵 Next' :
                     horizon === 'soon' ? '🟡 Soon' :
                     horizon === 'later' ? '⚪ Later' :
                     '💤 Someday'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Tags Filter */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4 flex items-center text-base">
              <svg className="w-5 h-5 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Tags
              {tagsFilter.length > 0 && (
                <span className="ml-3 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  {tagsFilter.length} selected
                </span>
              )}
            </h4>
            <div className="space-y-3 pl-8 max-h-48 overflow-y-auto">
              {availableTags.length > 0 ? availableTags.map(tag => (
                <label key={tag} className="flex items-center group cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tagsFilter.includes(tag)}
                    onChange={() => onTagToggle(tag)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-colors w-4 h-4"
                  />
                  <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                    #{tag}
                  </span>
                </label>
              )) : (
                <p className="text-sm text-gray-500 italic">No tags available</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h5 className="text-sm font-medium text-gray-900 mb-3">Active Filters:</h5>
          <div className="flex flex-wrap gap-2">
            {taskTypeFilter !== 'all' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                {taskTypeFilter === 'parent_only' ? '📋 Parent Tasks' : '📝 Subtasks'}
              </span>
            )}
            {statusFilter.length < Object.values(TaskStatus).length && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                Status: {statusFilter.length}/{Object.values(TaskStatus).length}
              </span>
            )}
            {horizonFilter.map(horizon => (
              <span key={horizon} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                {horizon === 'now' ? '🔴' : horizon === 'next' ? '🔵' : horizon === 'soon' ? '🟡' : horizon === 'later' ? '⚪' : '💤'} {horizon}
              </span>
            ))}
            {tagsFilter.map(tag => (
              <span key={tag} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel; 